import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AssignmentEntity,
  AssignmentStudentEntity
} from '../../../meta-data/app-meta-data.service';
import { AssignmentRecipientOptions } from '../models/assignment-recipient-options.model';
import { AssignmentStudentEligibility } from '../models/assignment-student-eligibility.model';
import { AssignmentRecipientOptionsService } from './assignment-recipient-options.service';

@Injectable({ providedIn: 'root' })
export class AssignmentStudentEligibilityService {
  constructor(private assignmentRecipientOptionsService: AssignmentRecipientOptionsService) {}

  evaluate(assignment: AssignmentEntity): Observable<AssignmentStudentEligibility> {
    const courseId = Number(assignment?.CourseId || 0);

    if (assignment?.UseGroupAssignment) {
      const groupId = Number(assignment.FlightSchoolGroupId || 0);
      if (!groupId) {
        return of(this.createUncheckedResult(0, courseId > 0));
      }

      return this.assignmentRecipientOptionsService.getOptions().pipe(map(options => {
        const group = (options.Groups || []).find(option => option.FlightSchoolGroupId === groupId);
        const studentIds = group?.StudentIds || [];
        return courseId
          ? this.createResult(options, studentIds, courseId)
          : this.createUncheckedResult(studentIds.length, false);
      }));
    }

    const studentIds = this.resolveSelectedStudentIds(assignment);
    if (studentIds.length === 0) {
      return of(this.createUncheckedResult(0, courseId > 0));
    }

    if (!courseId) {
      return of(this.createUncheckedResult(studentIds.length, false));
    }

    return this.assignmentRecipientOptionsService.getOptions()
      .pipe(map(options => this.createResult(options, studentIds, courseId)));
  }

  private resolveSelectedStudentIds(assignment: AssignmentEntity): number[] {
    return ((assignment?.AssignmentStudent || []) as AssignmentStudentEntity[])
      .filter(student => student._isDeleted !== true && !!student.StudentId)
      .map(student => Number(student.StudentId))
      .filter((studentId, index, studentIds) => studentIds.indexOf(studentId) === index);
  }

  private createResult(
    options: AssignmentRecipientOptions,
    studentIds: number[],
    courseId: number
  ): AssignmentStudentEligibility {
    const requestedStudentIds = studentIds || [];
    const totalStudentCount = requestedStudentIds.length;
    const requestedStudentIdSet = new Set(requestedStudentIds);
    const validStudentCount = (options.Students || []).filter(student =>
      requestedStudentIdSet.has(student.StudentId)
      && (student.ActiveCourseIds || []).indexOf(courseId) >= 0).length;
    const ignoredStudentCount = Math.max(totalStudentCount - validStudentCount, 0);

    return {
      courseSelected: true,
      validStudentCount,
      ignoredStudentCount,
      totalStudentCount,
      message: this.resolveMessage(validStudentCount, ignoredStudentCount)
    };
  }

  private createUncheckedResult(studentCount: number, courseSelected: boolean): AssignmentStudentEligibility {
    return {
      courseSelected,
      validStudentCount: studentCount,
      ignoredStudentCount: 0,
      totalStudentCount: studentCount,
      message: courseSelected ? '' : 'Select a course to check student eligibility.'
    };
  }

  private resolveMessage(validStudentCount: number, ignoredStudentCount: number): string {
    if (validStudentCount === 0) {
      return 'No selected students have the selected course.';
    }

    if (ignoredStudentCount > 0) {
      const ignoredLabel = ignoredStudentCount === 1 ? '1 student' : `${ignoredStudentCount} students`;
      return `${ignoredLabel} will be ignored because they do not have the selected course.`;
    }

    return '';
  }
}
